/**
 * RMS default region data - SINGLE SOURCE OF TRUTH for static region payload.
 * This file has no dependency on locale-config constants or types.
 * All constants derived from this data live in locale-config.derived-constants.
 */

/** Raw RMS API response format (uppercase) - matches actual RMS API response structure
 * @example { regionId: 101, regionCode: 'US', defaultLanguageIsoCode: 'en-US', ... }
 */
export interface RawRMSRegionDetail {
  readonly regionId: number
  readonly regionCode: string
  // Use case: keeps canonical regionCode stable while allowing user-facing URL/display code.
  readonly regionDisplayCode?: string
  readonly internalName: string
  readonly displayName: string
  readonly defaultLanguageIsoCode: string
  readonly defaultCurrencyIsoCode: string
  readonly servingAwsRegion: string
  readonly isPublished: boolean
  readonly supportedLanguagesIsoCodes: readonly string[]
  readonly supportedLanguages: readonly {
    readonly isoCode: string
    readonly displayName: string
  }[]
  readonly customAttributeValues?: {
    readonly defaultCurrencySymbol?: string
    readonly gladlyCurrencySymbol?: string
    readonly isTaxIncludedInSalePrice?: string
    /** Region-specific base URL for downstream API calls (e.g. customer/settings). */
    readonly regionalAPIBaseURL?: string
  }
}

/** Static region data type: RMS fields + flag URL (CDN) */
type StaticRegionDetail = RawRMSRegionDetail & { readonly flag: string }

/** Static region data from RMS API (uppercase format) - used when API fails.
 * All derived constants (SUPPORTED_COUNTRIES, COUNTRY_CURRENCIES, etc.) are computed from this in locale-config.derived-constants.
 */
export const DEFAULT_RMS_REGION_DATA = [
  {
    regionId: 101,
    regionCode: 'US',
    regionDisplayCode: 'US',
    internalName: 'United States',
    displayName: 'United States',
    defaultLanguageIsoCode: 'en-US',
    defaultCurrencyIsoCode: 'USD',
    servingAwsRegion: 'us-east-1',
    isPublished: true,
    supportedLanguagesIsoCodes: ['en-US'],
    supportedLanguages: [
      {
        isoCode: 'en-US',
        displayName: 'English',
      },
    ],
    flag: 'https://images.quince.com/6JC9fwzJ9hnUjU5RQQmPPf/fa5920b601ed053b5a6a9c608addfce6/usa.inline.svg',
  },
  {
    regionId: 102,
    regionCode: 'CA',
    regionDisplayCode: 'CA',
    internalName: 'Canada',
    displayName: 'Canada',
    defaultLanguageIsoCode: 'en-CA',
    defaultCurrencyIsoCode: 'CAD',
    servingAwsRegion: 'us-east-1',
    isPublished: true,
    supportedLanguagesIsoCodes: ['en-CA', 'fr-CA'],
    supportedLanguages: [
      {
        isoCode: 'en-CA',
        displayName: 'English',
      },
      {
        isoCode: 'fr-CA',
        displayName: 'Français',
      },
    ],
    flag: 'https://images.quince.com/5ge9sl9nyj8FRvRvhDzpB2/5ad89a2a5d431bb322eeeca46656336b/canada.inline.svg',
  },
  {
    // UK: regionCode is 'GB' per standards; additionalRegionCode 'UK' for URL/label (e.g. /uk).
    regionId: 103,
    regionCode: 'GB',
    regionDisplayCode: 'UK',
    internalName: 'United Kingdom',
    displayName: 'United Kingdom',
    defaultLanguageIsoCode: 'en-GB',
    defaultCurrencyIsoCode: 'GBP',
    servingAwsRegion: 'eu-west-1',
    isPublished: false,
    supportedLanguagesIsoCodes: ['en-GB'],
    supportedLanguages: [
      {
        isoCode: 'en-GB',
        displayName: 'English',
      },
    ],
    flag: 'https://images.quince.com/7h1uo4QzTSbVzVUrkNzFV3/8945f6026edcb82bcbb4d08e8455bf04/United_Kingdom__GB_.svg',
  },
] as const satisfies readonly StaticRegionDetail[]
